import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { FC, ReactNode, forwardRef, useEffect, useState } from 'react'
import { appConfig } from '../../config'
import useUnit, { useWeightUnit } from '../../hooks/useUnit'
import { showMessage } from './Helpers'

export const cmToIn = (v: number) =>
  parseFloat(
    (v / 2.54).toFixed(appConfig.fractionDigits).replace(/[.,]000$/, '')
  )
export const inToCm = (v: number) =>
  parseFloat(
    (v * 2.54).toFixed(appConfig.fractionDigits).replace(/[.,]000$/, '')
  )
export const cmToInWithoutRound = (v: number) => v / 2.54
export const inToCmWithoutRound = (v: number) => v * 2.54
export const kgToLbs = (v: number) =>
  parseFloat(
    (v * 2.20462).toFixed(appConfig.fractionDigits).replace(/[.,]000$/, '')
  )
export const lbsToKg = (v: number) =>
  parseFloat(
    (v / 2.20462).toFixed(appConfig.fractionDigits).replace(/[.,]000$/, '')
  )
export const kgToLbsWithoutRound = (v: number) => v * 2.20462
export const lbsToKgWithoutRound = (v: number) => v / 2.20462

interface ISelectProps {
  label: string
  valueInCm: number
  valuesInCm: number[]
  width?: string

  setValueInCm(v: number): void
}

export const SelectWithUnitSupport: FC<ISelectProps> = (props) => {
  const { unit } = useUnit()

  const [value, setValue] = useState(
    unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm)
  )
  const [values, setValues] = useState(
    unit === 'cm' ? props.valuesInCm : props.valuesInCm.map((v) => cmToIn(v))
  )

  useEffect(() => {
    setValue(unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm))
    setValues(
      unit === 'cm' ? props.valuesInCm : props.valuesInCm.map((v) => cmToIn(v))
    )
  }, [props.valuesInCm, props.valueInCm, unit])

  const handleChangeValue = (e: SelectChangeEvent<number>) => {
    const value = parseFloat(e.target.value.toString())
    props.setValueInCm(unit === 'cm' ? value : inToCm(value))
  }

  if (!unit) return <></>
  return (
    <FormControl sx={{ width: props.width || 1 }}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        defaultValue={value}
        value={value}
        label={props.label}
        onChange={handleChangeValue}
        startAdornment={
          <InputAdornment position="start">{unit}</InputAdornment>
        }
      >
        {values.map((v) => (
          <MenuItem value={v} key={'w' + v}>
            {v}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

interface IHeightSelectProps extends ISelectProps {
  cabHeight: number
  legHeight: number
  corner?: boolean
}

export const HeightSelectWithUnitSupport: FC<IHeightSelectProps> = (props) => {
  const { unit } = useUnit()

  return (
    <Tooltip
      title={
        <div>
          Cabinet height -{' '}
          {unit === 'cm' ? props.cabHeight : cmToIn(props.cabHeight)}
          {unit}
          <br />
          Legs height -{' '}
          {unit === 'cm' ? props.legHeight : cmToIn(props.legHeight)}
          {unit}
        </div>
      }
      arrow
      placement="top"
    >
      <div style={{ width: props.corner ? '30%' : '100%' }}>
        <SelectWithUnitSupport
          label="Height"
          valueInCm={props.valueInCm}
          valuesInCm={props.valuesInCm}
          setValueInCm={props.setValueInCm}
        />
      </div>
    </Tooltip>
  )
}

export const HeightInputWithUnitSupport: FC<IHeightSelectProps> = (props) => {
  const { unit } = useUnit()

  return (
    <Tooltip
      title={
        <div>
          Cabinet height -{' '}
          {unit === 'cm' ? props.cabHeight : cmToIn(props.cabHeight)}
          {unit}
          <br />
          Legs height -{' '}
          {unit === 'cm' ? props.legHeight : cmToIn(props.legHeight)}
          {unit}
        </div>
      }
      arrow
      placement="top"
    >
      <div style={{ width: props.corner ? '30%' : '100%' }}>
        <InputWithUnitSupport
          label="Height"
          valueInCm={props.valueInCm}
          setValueInCm={props.setValueInCm}
        />
      </div>
    </Tooltip>
  )
}

interface IInputProps {
  onEnter?: boolean
  // isNumber?:boolean
  valueInCm: number
  label: string
  width?: number | string
  size?: 'medium' | 'small'
  disabled?: boolean

  setValueInCm(v: number): void

  sx?: SxProps
}

export const InputWithUnitSupport: FC<IInputProps> = (props) => {
  const { unit } = useUnit()

  const [value, setValue] = useState(
    unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm)
  )

  useEffect(() => {
    setValue(unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm))
  }, [props.valueInCm, unit])

  const handleBlur = () => {
    const newValueInCm = unit === 'cm' ? value : inToCm(value)

    if (newValueInCm !== props.valueInCm) {
      props.setValueInCm(newValueInCm)
    }
  }

  return (
    <FormControl variant="outlined" size={props.size} sx={{ ...props.sx }}>
      <InputLabel>{props.label}</InputLabel>
      <OutlinedInput
        style={{ width: props.width }}
        type="number"
        label={props.label}
        value={value}
        onChange={(e) => {
          const pervValue = value
          let newValue = parseFloat(e.target.value)

          setValue(newValue)

          if (Math.abs(pervValue - newValue) === 1) handleBlur() // onBlur(newValue)
        }}
        onBlur={handleBlur}
        onKeyUp={(e) => {
          if (e.key === 'Enter') handleBlur()
        }}
        onFocus={(e) => e.target.select()}
        startAdornment={
          <InputAdornment position="start">{unit}</InputAdornment>
        }
        inputProps={{ min: 0 }}
        disabled={props.disabled}
      />
    </FormControl>
  )
}

interface ISectionHeightInfo {
  valueInCm: number

  onClick(): void
  sx?: SxProps
}

export const SectionHeightInfoWithUnitSupport = forwardRef<
  any,
  ISectionHeightInfo
>((props, ref) => {
  const { unit } = useUnit()

  return (
    <Typography ref={ref} sx={{ ...props.sx }} onClick={props.onClick}>
      {unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm)} {unit}
    </Typography>
  )
})

interface ITempSectionHeightInput {
  valueInCm: number

  onChange(v: number): void
  onSubmit(): void
  sx?: SxProps
}

export const TempSectionHeightInputWithUnitSupport: FC<
  ITempSectionHeightInput
> = (props) => {
  const { unit } = useUnit()

  return (
    <FormControl variant="outlined" size="small" sx={{ ...props.sx }}>
      <OutlinedInput
        style={{ width: 120 }}
        type="number"
        value={unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm)}
        onChange={(e) => {
          const newValue = parseFloat(e.target.value)
          props.onChange(unit === 'cm' ? newValue : inToCm(newValue))
        }}
        onKeyUp={(e) => {
          if (e.key === 'Enter') props.onSubmit()
        }}
        onBlur={props.onSubmit}
        onFocus={(e) => e.target.select()}
        startAdornment={
          <InputAdornment position="start">{unit}</InputAdornment>
        }
        inputProps={{ min: 0 }}
        autoFocus
        // disabled={this.state.tempSectionPosesValues.length === 1}
      />
    </FormControl>
  )
}

interface IInputWithoutSubmit {
  label: string
  valueInCm: number
  width?: number | string
  size?: 'small'
  disabled?: boolean

  onChange?: (v: number) => void

  sx?: SxProps
}

export const InputWithoutSubmitWithUnitSupport: FC<IInputWithoutSubmit> = (
  props
) => {
  const { unit } = useUnit()

  return (
    <FormControl variant="outlined" size={props.size} sx={{ ...props.sx }}>
      <InputLabel>{props.label}</InputLabel>
      <OutlinedInput
        style={{ width: props.width }}
        type="number"
        label={props.label}
        value={unit === 'cm' ? props.valueInCm : cmToIn(props.valueInCm)}
        onChange={
          props.onChange
            ? (e) => {
                const newValue = parseFloat(e.target.value)
                if (props.onChange)
                  props.onChange(unit === 'cm' ? newValue : inToCm(newValue))
              }
            : undefined
        }
        startAdornment={
          <InputAdornment position="start">{unit}</InputAdornment>
        }
        disabled={props.disabled}
      />
    </FormControl>
  )
}

interface IHardwareMUPAWithUnitSupport {
  valueInCm: number
  width?: number | string
  size?: 'small'

  onChange?: (v: number) => void

  sx?: SxProps
}

export const HardwareMUPAWithUnitSupport: FC<IHardwareMUPAWithUnitSupport> = (
  props
) => {
  const { unit } = useUnit()

  return (
    <FormControl variant="outlined" size={props.size} sx={{ ...props.sx }}>
      <InputLabel>Machining Unit Price Addon</InputLabel>
      <OutlinedInput
        style={{ width: props.width }}
        type="number"
        label="Machining Unit Price Addon"
        value={
          unit === 'cm' ? props.valueInCm : cmToInWithoutRound(props.valueInCm)
        }
        onChange={
          props.onChange
            ? (e) => {
                const newValue = parseFloat(e.target.value)
                if (props.onChange)
                  props.onChange(
                    unit === 'cm' ? newValue : inToCmWithoutRound(newValue)
                  )
              }
            : undefined
        }
        startAdornment={
          <InputAdornment position="start">$/{unit}</InputAdornment>
        }
      />
    </FormControl>
  )
}

interface ICategorySizeAllValuesProps {
  valuesInCm: number[]

  handleEditCancel(): void
  handleEditApply(valuesInCm: number[]): void

  width?: number | string
  sx?: SxProps
  isCMOnly?: boolean
}

const CategorySizeAllValuesWithUnitSupport: FC<ICategorySizeAllValuesProps> = (
  props
) => {
  const { unit: trueUnit } = useUnit()
  const unit = props.isCMOnly ? 'cm' : trueUnit
  const { enqueueSnackbar } = useSnackbar()

  const [allValues, setAllValues] = useState(
    unit === 'cm'
      ? props.valuesInCm.join(',')
      : props.valuesInCm.map((v) => cmToIn(v)).join(',')
  )

  const handleEditApply = () => {
    const acceptableSymbols = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      ',',
      ' ',
      '.',
    ]

    for (const char of allValues) {
      if (!acceptableSymbols.includes(char)) {
        showMessage(enqueueSnackbar, 'Invalid character')
        return
      }
    }

    const values = allValues.split(',').map((v) => parseFloat(v))

    props.handleEditApply(unit === 'cm' ? values : values.map((v) => inToCm(v)))

    // props.setValues(values)

    // setEditMode(false)
  }

  useEffect(() => {
    setAllValues(
      unit === 'cm'
        ? props.valuesInCm.join(',')
        : props.valuesInCm.map((v) => cmToIn(v)).join(',')
    )
  }, [unit, props.valuesInCm])

  return (
    <FormControl variant="outlined" sx={{ ...props.sx }}>
      <InputLabel>All Values</InputLabel>
      <OutlinedInput
        style={{ width: props.width }}
        label="All Values"
        multiline
        value={allValues}
        onChange={(e) => {
          setAllValues(e.target.value)
        }}
        endAdornment={
          <Box display="flex">
            <IconButton
              size="small"
              onClick={props.handleEditCancel}
              color="error"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleEditApply}>
              <CheckIcon fontSize="small" color="success" />
            </IconButton>
          </Box>
        }
        onFocus={(e) => e.target.select()}
      />
    </FormControl>
  )
}

export default CategorySizeAllValuesWithUnitSupport

interface IInputWithoutSubmitWeight {
  label: string
  valueInKg: number
  width?: number | string
  size?: 'small'
  disabled?: boolean
  endAdornment?: ReactNode

  onChange?: (v: number) => void

  sx?: SxProps
}

export const InputWithoutSubmitWithWeightUnitSupport: FC<
  IInputWithoutSubmitWeight
> = (props) => {
  const { unit } = useWeightUnit()

  return (
    <FormControl variant="outlined" size={props.size} sx={{ ...props.sx }}>
      <InputLabel>{props.label}</InputLabel>
      <OutlinedInput
        style={{ width: props.width }}
        type="number"
        label={props.label}
        value={
          unit === 'kg' ? props.valueInKg : kgToLbsWithoutRound(props.valueInKg)
        }
        onChange={
          props.onChange
            ? (e) => {
                const newValue = parseFloat(e.target.value)
                if (props.onChange)
                  props.onChange(
                    unit === 'kg' ? newValue : lbsToKgWithoutRound(newValue)
                  )
              }
            : undefined
        }
        startAdornment={
          <InputAdornment position="start">{unit}</InputAdornment>
        }
        disabled={props.disabled}
        endAdornment={props.endAdornment}
      />
    </FormControl>
  )
}
